import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';

export type McGreetingAudience = 'candidate' | 'employer';

type PoolEntry = { slug: string; hasName: boolean };

// Candidate pool — all entries EXCEPT `hiring_doesnt_hurt`.
const CANDIDATE_POOL: readonly PoolEntry[] = [
  { slug: 'lets_get_into_it', hasName: true },
  { slug: 'ai_on_your_side', hasName: false },
  { slug: 'team_up_with_bestie', hasName: false },
  { slug: 'coffee_and_bestie_time', hasName: false },
  { slug: 'never_hunt_alone', hasName: false },
  { slug: 'refresh_work', hasName: false },
  { slug: 'where_should_we_begin', hasName: false },
  { slug: 'your_move', hasName: true },
  { slug: 'mic_is_yours', hasName: true },
];

// Employer pool — candidate pool plus `hiring_doesnt_hurt`.
const EMPLOYER_POOL: readonly PoolEntry[] = [
  ...CANDIDATE_POOL,
  { slug: 'hiring_doesnt_hurt', hasName: false },
  { slug: 'we_interview', hasName: false },
];

const pickRandom = <T,>(pool: readonly T[]): T => pool[Math.floor(Math.random() * pool.length)];

const firstToken = (raw: string | undefined): string | undefined => {
  const trimmed = raw?.trim();
  if (!trimmed) return undefined;
  const [first] = trimmed.split(/\s+/);
  return first || undefined;
};

/**
 * Picks a random greeting for the Mission Control home page heading.
 *
 * The pool entry is chosen once per mount. The translation is derived reactively so that
 * the user's first name appears as soon as the Redux `headerInfo` slice is populated by
 * the existing SWR fetch — without re-rolling the pool entry, which would flicker.
 * Returns `{ message: null }` during SSR and the first client paint so the consumer can
 * reserve layout height.
 */
export const useMcHomeGreeting = (audience: McGreetingAudience): { message: string | null } => {
  const { t } = useTranslation('common');
  const headerInfo = useAppSelector(getUserHeaderInfo);
  const [entry, setEntry] = useState<PoolEntry | null>(null);

  useEffect(() => {
    const pool = audience === 'candidate' ? CANDIDATE_POOL : EMPLOYER_POOL;
    setEntry(pickRandom(pool));
  }, [audience]);

  return useMemo(() => {
    if (!entry) return { message: null };
    const rawName = audience === 'candidate' ? headerInfo?.candidateName : headerInfo?.name;
    const firstName = firstToken(rawName);
    if (entry.hasName && firstName) {
      return { message: t(`mc.greeting.${entry.slug}`, { name: firstName }) };
    }
    if (entry.hasName) {
      return { message: t(`mc.greeting.${entry.slug}.guest`) };
    }
    return { message: t(`mc.greeting.${entry.slug}`) };
  }, [entry, audience, headerInfo, t]);
};
